import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { V2Sidebar } from "./V2Sidebar";
import { V2Header } from "./V2Header";
import { V2MobileNav } from "./V2MobileNav";
import { CommandPalette } from "@/v2/components/CommandPalette";
import { useRole } from "@/contexts/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { getV2HomeForRole, canAccessV2Path } from "@/v2/lib/v2Roles";

export function V2Layout() {
  const [cmdOpen, setCmdOpen] = useState(false);
  // Sidebar rétractable (desktop) — persistée pour libérer de la largeur.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("skyline-v2-sidebar-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleSidebar = () =>
    setSidebarCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem("skyline-v2-sidebar-collapsed", next ? "1" : "0");
      } catch {
        /* quota / privacy mode → ignore */
      }
      return next;
    });
  const { role } = useRole();
  const { profile, isDevSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Garde-fou d'autorisation : le rôle n'est fiable qu'une fois le profil chargé
  // (sinon RoleContext retombe sur "collaborateur" pendant le chargement → course).
  // Tant que le profil n'est pas là, on ne redirige pas (évite de rebondir à tort).
  const roleReady = isDevSession || !!profile;

  // Cmd+K / Ctrl+K → ouvre la palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
      if (e.key === "Escape") setCmdOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Redirection home si on est sur /v2 nu
  useEffect(() => {
    if (location.pathname === "/v2" || location.pathname === "/v2/") {
      navigate(getV2HomeForRole(role), { replace: true });
    }
  }, [location.pathname, role, navigate]);

  // Rôle connu mais route interdite → redirection vers sa home (rendu synchrone,
  // la page interdite n'est jamais montée → pas de fuite de données).
  if (roleReady && !canAccessV2Path(role, location.pathname)) {
    return <Navigate to={getV2HomeForRole(role)} replace />;
  }

  return (
    <div className="flex h-screen bg-background">
      {!sidebarCollapsed && <V2Sidebar />}
      {/* h-screen (pas min-h-screen) sur le conteneur externe UNIQUEMENT —
          le doublon min-h-screen ici + sur l'externe pouvait, selon
          l'arrondi de 100vh (zoom/échelle d'écran), pousser le contenu de
          quelques px au-delà du viewport et faire apparaître un ascenseur
          fantôme sur certains écrans mais pas d'autres, même navigateur
          (signalé Thibaud 2026-08-20). pb-6 -> pb-4 sur desktop en plus,
          par prudence. */}
      <div className="flex-1 flex flex-col min-w-0">
        <V2Header
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <main className="flex-1 overflow-y-auto overflow-x-hidden pb-20 md:pb-4">
          <Outlet />
        </main>
      </div>
      <V2MobileNav />
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  );
}
